// <copyright company="Microsoft">Copyright (c) Microsoft. All rights reserved.</copyright>

import React, { useMemo, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import {
    Button,
    Card,
    CardHeader,
    Dropdown,
    Field,
    Input,
    Label,
    MessageBar,
    MessageBarBody,
    MessageBarTitle,
    Option,
    Textarea,
    Title3,
    Subtitle2,
    Text,
    makeStyles,
    shorthands,
    tokens,
} from '@fluentui/react-components';
import {
    AddRegular,
    DeleteRegular,
    DocumentAddRegular,
    CopyRegular,
    ArrowDownloadRegular,
    BuildingRegular,
    PersonRegular,
    DocumentBulletListRegular,
    MoneyRegular,
    LinkRegular,
    CodeRegular,
    DocumentTextRegular,
} from '@fluentui/react-icons';
import { saveAs } from 'file-saver';
import { usePageTitle } from '../../hooks/usePageTitle';
import {
    DEFAULT_COUNTRY_CODE,
    FRANCE_ADDRESS_TYPES,
    FRANCE_DOCUMENT_TYPES,
    FRANCE_LEGAL_ENTITIES,
    FRANCE_TERRITORY_COUNTRY_CODES,
    FRANCE_TERRITORY_CODE_SET,
    FRENCH_ENTITY_BUYERS,
    MANUAL_INVOICE_COUNTRIES,
    REQUIRED_CURRENCY,
    SUPPLY_TYPES,
    VALID_FRENCH_VAT_RATES,
    FranceAddressType,
    FranceDocumentType,
} from './franceConstants';
import { LineItemForm, ManualInvoiceFormState, ValidationError } from './types';
import { buildPayload } from './payloadBuilder';
import { validateForm } from './validation';
import { acquireAccessToken } from '../../utils/authToken';
import { validateManualInvoicePayload, BackendValidationError } from './manualInvoiceService';
import { toFriendlyError } from './manualInvoiceErrorMessages';

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.padding('24px'),
        ...shorthands.gap('16px'),
        backgroundColor: tokens.colorNeutralBackground2,
        minHeight: '100%',
        boxSizing: 'border-box',
        width: '100%',
        maxWidth: '1160px',
        marginLeft: 'auto',
        marginRight: 'auto',
    },
    header: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap('4px'),
        marginBottom: '4px',
    },
    headerRow: {
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        ...shorthands.gap('16px'),
        flexWrap: 'wrap',
    },
    headerSubtitle: {
        color: tokens.colorNeutralForeground3,
    },
    countrySelect: {
        minWidth: '220px',
    },
    fullWidth: {
        gridColumn: '1 / -1',
    },
    card: {
        ...shorthands.padding('18px', '20px'),
        ...shorthands.borderRadius(tokens.borderRadiusLarge),
        boxShadow: tokens.shadow4,
        transition: 'box-shadow 0.15s ease, transform 0.15s ease',
        ':hover': {
            boxShadow: tokens.shadow8,
        },
    },
    sectionBadge: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: '32px',
        height: '32px',
        ...shorthands.borderRadius(tokens.borderRadiusMedium),
        backgroundColor: tokens.colorBrandBackground2,
        color: tokens.colorBrandForeground2,
        fontSize: '18px',
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        ...shorthands.gap('14px'),
        marginTop: '14px',
    },
    lineItem: {
        display: 'grid',
        gridTemplateColumns: 'minmax(56px, 0.5fr) minmax(220px, 3fr) repeat(3, minmax(88px, 0.7fr)) auto',
        ...shorthands.gap('10px'),
        alignItems: 'end',
        ...shorthands.padding('12px'),
        ...shorthands.borderRadius(tokens.borderRadiusMedium),
        backgroundColor: tokens.colorNeutralBackground2,
        ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
        marginBottom: '10px',
        transition: 'background-color 0.15s ease, border-color 0.15s ease',
        ':hover': {
            backgroundColor: tokens.colorNeutralBackground2Hover,
            ...shorthands.borderColor(tokens.colorNeutralStroke1),
        },
    },
    actionRow: {
        display: 'flex',
        ...shorthands.gap('12px'),
        alignItems: 'center',
        position: 'sticky',
        bottom: '0',
        zIndex: 1,
        ...shorthands.padding('12px', '16px'),
        ...shorthands.borderRadius(tokens.borderRadiusLarge),
        backgroundColor: tokens.colorNeutralBackground1,
        boxShadow: tokens.shadow8,
    },
    output: {
        width: '100%',
        marginTop: '14px',
        '& textarea': {
            fontFamily: tokens.fontFamilyMonospace,
            fontSize: tokens.fontSizeBase200,
            backgroundColor: tokens.colorNeutralBackground3,
            lineHeight: tokens.lineHeightBase300,
        },
    },
    errorList: {
        marginTop: '4px',
        marginBottom: '0',
        paddingLeft: '20px',
    },
    errorGuidance: {
        marginTop: '2px',
    },
    errorDetail: {
        marginTop: '2px',
        color: tokens.colorNeutralForeground3,
        fontSize: tokens.fontSizeBase200,
    },
    totalsBar: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: '16px',
    },
    totalsBox: {
        display: 'flex',
        flexDirection: 'column',
        ...shorthands.gap('6px'),
        minWidth: '280px',
        ...shorthands.padding('14px', '18px'),
        ...shorthands.borderRadius(tokens.borderRadiusMedium),
        backgroundColor: tokens.colorNeutralBackground2,
        ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    },
    totalRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        ...shorthands.gap('24px'),
    },
    totalLabel: {
        color: tokens.colorNeutralForeground3,
    },
    totalValue: {
        fontFamily: tokens.fontFamilyMonospace,
    },
    grandTotalRow: {
        marginTop: '4px',
        paddingTop: '8px',
        ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
    },
    grandTotalValue: {
        fontFamily: tokens.fontFamilyMonospace,
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.colorNeutralForeground1,
    },
    noWrapButton: {
        whiteSpace: 'nowrap',
    },
});

function emptyLineItem(): LineItemForm {
    return {
        key: `li-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        lineItemId: '',
        description: '',
        quantity: '1',
        unitPrice: '',
        vatRate: '',
    };
}

function createInitialState(): ManualInvoiceFormState {
    return {
        documentType: 'invoice',
        invoiceTypeCode: '',
        documentId: '',
        documentDate: '',
        billingStartDate: '',
        billingEndDate: '',
        taxCalculationDate: '',
        transactionCurrencyCode: REQUIRED_CURRENCY,
        countryCode: DEFAULT_COUNTRY_CODE,
        companyCode: '',
        supplyType: '',
        purchaseOrderNumber: '',
        paymentDueDate: '',
        documentDescription: '',
        seller: {
            companyName: '',
            firstName: '',
            lastName: '',
            email: '',
            phoneNumber: '',
            vatId: '',
            siren: '',
            siret: '',
            address: { line1: '', line2: '', line3: '', city: '', postalCode: '', countryCode: DEFAULT_COUNTRY_CODE },
        },
        customerId: '',
        buyer: {
            companyName: '',
            firstName: '',
            email: '',
            phoneNumber: '',
            vatId: '',
            siren: '',
            siret: '',
            addressType: 'billTo',
            address: { line1: '', line2: '', line3: '', city: '', postalCode: '', countryCode: DEFAULT_COUNTRY_CODE },
        },
        lineItems: [emptyLineItem()],
        manualAmounts: { preTaxAmount: '', taxableAmount: '', taxAmount: '', totalAmount: '' },
        reference: { documentId: '', date: '' },
    };
}

const deepClone = <T,>(value: T): T =>
    typeof structuredClone === 'function'
        ? structuredClone(value)
        : (JSON.parse(JSON.stringify(value)) as T);

// Maps backend "checksSkipped" codes to a short human-readable label for the success caveat.
const SKIPPED_CHECK_LABELS: Record<string, string> = {
    GoLiveDateGating: 'country go-live date gating',
    SupplierRouting: 'supplier routing',
    PartnerAndTransportChecks: 'partner and transport checks',
};

function friendlySkippedCheck(code: string): string {
    return SKIPPED_CHECK_LABELS[code] ?? code;
}

function ManualInvoice(): JSX.Element {
    usePageTitle('Manual Invoice Creator');
    const styles = useStyles();

    const [form, setForm] = useState<ManualInvoiceFormState>(createInitialState);
    const [errors, setErrors] = useState<ValidationError[] | null>(null);
    const [payloadJson, setPayloadJson] = useState<string>('');
    const [copied, setCopied] = useState<boolean>(false);
    const [country, setCountry] = useState<string>('FR');
    const [isValidating, setIsValidating] = useState<boolean>(false);
    const [backendErrors, setBackendErrors] = useState<BackendValidationError[] | null>(null);
    const [checksSkipped, setChecksSkipped] = useState<string[]>([]);
    const [serviceError, setServiceError] = useState<string | null>(null);

    // Monotonic counter bumped on every form edit. A validation run captures the value at
    // start and aborts applying its result if the form changed while it was in flight, so a
    // stale (pre-edit) payload can never be surfaced for copy/download.
    const formGeneration = useRef(0);

    const { instance, accounts } = useMsal();

    const isPaymentReceipt = form.documentType === 'paymentReceipt';
    const isCreditNote = form.documentType === 'creditNote';
    const isTd17 = form.invoiceTypeCode === 'TD17';
    const documentTypeOptions = isTd17
        ? FRANCE_DOCUMENT_TYPES.filter((d) => d.value !== 'paymentReceipt')
        : FRANCE_DOCUMENT_TYPES;
    const countryOptions = FRANCE_TERRITORY_COUNTRY_CODES;

    const errorByField = useMemo(() => {
        const map = new Map<string, string>();
        (errors ?? []).forEach((e) => {
            if (!map.has(e.field)) {
                map.set(e.field, e.message);
            }
        });
        return map;
    }, [errors]);

    const totals = useMemo(() => {
        const p = buildPayload(form);
        return {
            preTax: p.preTaxAmount.totalAmount,
            tax: p.taxAmount.totalAmount,
            total: p.totalAmount.totalAmount,
        };
    }, [form]);

    const currency = form.transactionCurrencyCode.trim() || 'EUR';
    const formatMoney = (value: number): string =>
        `${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;

    const update = (mutator: (draft: ManualInvoiceFormState) => void): void => {
        // Any edit invalidates a previously generated/validated payload, so clear it to
        // prevent copying or downloading a stale payload that no longer reflects the form.
        // Bumping the generation also aborts any validation currently in flight.
        formGeneration.current += 1;
        setPayloadJson('');
        setBackendErrors(null);
        setChecksSkipped([]);
        setServiceError(null);
        setCopied(false);
        setForm((prev) => {
            const next = deepClone(prev);
            mutator(next);
            return next;
        });
    };

    const addLineItem = (): void => update((d) => { d.lineItems.push(emptyLineItem()); });
    const removeLineItem = (index: number): void => update((d) => { d.lineItems.splice(index, 1); });

    const handleCreatePayload = async (): Promise<void> => {
        setCopied(false);
        setServiceError(null);
        setBackendErrors(null);
        setChecksSkipped([]);

        // Stage 1: client-side validation gives immediate, field-level feedback.
        const found = validateForm(form);
        setErrors(found);
        if (found.length > 0) {
            setPayloadJson('');
            return;
        }

        // Stage 2: authoritative backend validation. The payload is only surfaced for
        // copy/download once the backend confirms it passes the real France e-invoicing
        // content and business rules. This is not a guarantee of acceptance — submission-time
        // checks reported in checksSkipped still apply at ingestion. On any transport/auth
        // failure we block (rather than silently falling back to client-only checks).
        // We build the payload once and surface that exact object, so the JSON the user
        // copies is byte-identical to what the backend validated.
        const payload = buildPayload(form);
        const generation = formGeneration.current;
        setIsValidating(true);
        try {
            const token = await acquireAccessToken(instance, accounts[0]);
            if (formGeneration.current !== generation) {
                return; // Form was edited while acquiring the token; abort stale result.
            }
            if (!token) {
                setServiceError('Could not acquire an access token to reach the validation service. Please retry.');
                setPayloadJson('');
                return;
            }

            const result = await validateManualInvoicePayload(payload, token);
            if (formGeneration.current !== generation) {
                return; // Form was edited while validating; abort stale result.
            }
            if (result.isValid) {
                setBackendErrors(null);
                setChecksSkipped(result.checksSkipped ?? []);
                setPayloadJson(JSON.stringify(payload, null, 2));
            } else {
                setBackendErrors(result.errors);
                setChecksSkipped([]);
                setPayloadJson('');
            }
        } catch (e) {
            if (formGeneration.current !== generation) {
                return; // Form was edited; the error belongs to a superseded run.
            }
            setServiceError(
                `Could not reach the validation service, so the payload was not created. ${(e as Error).message ?? ''}`.trim()
            );
            setPayloadJson('');
        } finally {
            setIsValidating(false);
        }
    };

    const handleCopy = async (): Promise<void> => {
        if (!payloadJson) {
            return;
        }
        await navigator.clipboard.writeText(payloadJson);
        setCopied(true);
    };

    const handleDownload = (): void => {
        if (!payloadJson) {
            return;
        }
        const name = (form.documentId || 'manual-invoice').replace(/[^\w.-]/g, '_');
        const blob = new Blob([payloadJson], { type: 'application/json;charset=utf-8' });
        saveAs(blob, `${name}.json`);
    };

    const err = (field: string): string | undefined => errorByField.get(field);

    return (
        <div className={styles.root}>
            <div className={styles.header}>
                <div className={styles.headerRow}>
                    <Title3>Manual Invoice Creator</Title3>
                    <div className={styles.countrySelect}>
                        <Field label="Country / reporting regime">
                            <Dropdown
                                value={MANUAL_INVOICE_COUNTRIES.find((c) => c.code === country)?.label ?? ''}
                                selectedOptions={[country]}
                                onOptionSelect={(_, data) => {
                                    const picked = MANUAL_INVOICE_COUNTRIES.find((c) => c.code === data.optionValue);
                                    if (picked?.enabled) {
                                        setCountry(picked.code);
                                    }
                                }}
                            >
                                {MANUAL_INVOICE_COUNTRIES.map((c) => (
                                    <Option key={c.code} value={c.code} disabled={!c.enabled}>{c.label}</Option>
                                ))}
                            </Dropdown>
                        </Field>
                    </div>
                </div>
                <Text size={200} className={styles.headerSubtitle}>Enter a France transaction and generate a ready-to-submit payload. Fields and checks follow the France e-reporting requirements.</Text>
            </div>

            {/* Document header */}
            <Card className={styles.card}>
                <CardHeader image={<span className={styles.sectionBadge}><DocumentTextRegular /></span>} header={<Subtitle2>Document</Subtitle2>} description={<Text size={200}>Header details for the transaction</Text>} />
                <div className={styles.grid}>
                    <Field label="Document type" required>
                        <Dropdown
                            value={FRANCE_DOCUMENT_TYPES.find((d) => d.value === form.documentType)?.label ?? ''}
                            selectedOptions={[form.documentType]}
                            onOptionSelect={(_, data) => update((d) => { d.documentType = data.optionValue as FranceDocumentType; })}
                        >
                            {documentTypeOptions.map((d) => (
                                <Option key={d.value} value={d.value}>{d.label}</Option>
                            ))}
                        </Dropdown>
                    </Field>
                    <Field label="Document ID" required validationMessage={err('documentId')}>
                        <Input value={form.documentId} onChange={(_, d) => update((s) => { s.documentId = d.value; })} />
                    </Field>
                    {isTd17 ? (
                        <Field label="Seller company code (non-French)" required validationMessage={err('companyCode')} hint="Not a France-registered or MIOL code.">
                            <Input value={form.companyCode} onChange={(_, d) => update((s) => { s.companyCode = d.value.trim(); })} />
                        </Field>
                    ) : (
                        <Field label="Company code" required validationMessage={err('companyCode')}>
                            <Dropdown
                                placeholder="Select legal entity"
                                value={(() => {
                                    const e = FRANCE_LEGAL_ENTITIES.find((x) => x.companyCode === form.companyCode);
                                    return e ? `${e.companyCode} — ${e.legalName}` : '';
                                })()}
                                selectedOptions={form.companyCode ? [form.companyCode] : []}
                                onOptionSelect={(_, data) => update((s) => {
                                    s.companyCode = data.optionValue ?? '';
                                    const ent = FRANCE_LEGAL_ENTITIES.find((x) => x.companyCode === data.optionValue);
                                    if (ent) {
                                        s.seller.companyName = ent.legalName;
                                        s.seller.siren = ent.siren;
                                        s.seller.vatId = ent.vatId;
                                    }
                                })}
                            >
                                {FRANCE_LEGAL_ENTITIES.map((e) => (
                                    <Option key={e.companyCode} value={e.companyCode}>{`${e.companyCode} — ${e.legalName}`}</Option>
                                ))}
                            </Dropdown>
                        </Field>
                    )}
                    <Field label="Country code" required validationMessage={err('countryCode')}>
                        <Dropdown
                            value={countryOptions.find((c) => c.code === form.countryCode)?.name ?? form.countryCode}
                            selectedOptions={[form.countryCode]}
                            onOptionSelect={(_, data) => update((s) => { s.countryCode = data.optionValue ?? ''; })}
                        >
                            {countryOptions.map((c) => (
                                <Option key={c.code} value={c.code}>{`${c.code} — ${c.name}`}</Option>
                            ))}
                        </Dropdown>
                    </Field>
                    <Field label="Currency" required validationMessage={err('transactionCurrencyCode')}>
                        <Input value={form.transactionCurrencyCode} onChange={(_, d) => update((s) => { s.transactionCurrencyCode = d.value.toUpperCase(); })} />
                    </Field>
                    <Field label="Supply type">
                        <Dropdown
                            placeholder="(default B2B)"
                            value={form.supplyType}
                            selectedOptions={form.supplyType ? [form.supplyType] : []}
                            onOptionSelect={(_, data) => update((s) => { s.supplyType = data.optionValue ?? ''; })}
                        >
                            {SUPPLY_TYPES.map((t) => (<Option key={t} value={t}>{t}</Option>))}
                        </Dropdown>
                    </Field>
                    <Field label="Document date" required validationMessage={err('documentDate')}>
                        <Input type="date" value={form.documentDate} onChange={(_, d) => update((s) => { s.documentDate = d.value; })} />
                    </Field>
                    <Field label="Billing start date">
                        <Input type="date" value={form.billingStartDate} onChange={(_, d) => update((s) => { s.billingStartDate = d.value; })} />
                    </Field>
                    <Field label="Billing end date">
                        <Input type="date" value={form.billingEndDate} onChange={(_, d) => update((s) => { s.billingEndDate = d.value; })} />
                    </Field>
                    <Field label="Tax calculation date">
                        <Input type="date" value={form.taxCalculationDate} onChange={(_, d) => update((s) => { s.taxCalculationDate = d.value; })} />
                    </Field>
                    <Field label="Payment due date">
                        <Input type="date" value={form.paymentDueDate} onChange={(_, d) => update((s) => { s.paymentDueDate = d.value; })} />
                    </Field>
                    <Field label="Purchase order number">
                        <Input value={form.purchaseOrderNumber} onChange={(_, d) => update((s) => { s.purchaseOrderNumber = d.value; })} />
                    </Field>
                    <div className={styles.fullWidth}>
                        <Field label="Notes / description" hint="Free-text note carried into the report (mapped to the document description / NOTES block).">
                            <Textarea
                                value={form.documentDescription}
                                onChange={(_, d) => update((s) => { s.documentDescription = d.value; })}
                                rows={2}
                                resize="vertical"
                                placeholder="Optional free-text note for this document"
                            />
                        </Field>
                    </div>
                    <Field label="Transaction scenario" hint="TD17 reports a purchase from a foreign supplier to a French Microsoft entity.">
                        <Dropdown
                            value={isTd17 ? 'TD17 — Purchase report' : 'Standard sales'}
                            selectedOptions={[isTd17 ? 'TD17' : '']}
                            onOptionSelect={(_, data) => update((s) => {
                                const v = data.optionValue === 'TD17' ? 'TD17' : '';
                                s.invoiceTypeCode = v;
                                if (v === 'TD17' && s.documentType === 'paymentReceipt') {
                                    s.documentType = 'invoice';
                                }
                                // Header country must always be FR or a French territory.
                                // MQ/GP/RE are domestic SoldTo codes the EDICOM France
                                // validator rejects as a header country (the options list
                                // already excludes them); coerce anything invalid to FR.
                                if (!FRANCE_TERRITORY_CODE_SET.has(s.countryCode)) {
                                    s.countryCode = 'FR';
                                }
                            })}
                        >
                            <Option value="">Standard sales</Option>
                            <Option value="TD17">TD17 — Purchase report (foreign supplier)</Option>
                        </Dropdown>
                    </Field>
                    {isTd17 && (
                        <div className={styles.fullWidth}>
                            <MessageBar intent="info">
                                <MessageBarBody>
                                    <MessageBarTitle>Purchase report (TD17)</MessageBarTitle>
                                    Roles are reversed: the <b>Seller</b> is the non-French supplier, and the <b>Buyer</b> must be one of the French Microsoft entities (its SIREN or VAT ID determines routing). The company code field holds the non-French seller code.
                                </MessageBarBody>
                            </MessageBar>
                        </div>
                    )}
                </div>
            </Card>

            {/* Seller */}
            <Card className={styles.card}>
                <CardHeader image={<span className={styles.sectionBadge}><BuildingRegular /></span>} header={<Subtitle2>{isTd17 ? 'Seller (foreign supplier)' : 'Seller (supplier)'}</Subtitle2>} description={<Text size={200}>{isTd17 ? 'The non-French supplier that issued the purchase invoice' : 'The Microsoft legal entity issuing the document'}</Text>} />
                <div className={styles.grid}>
                    <Field label="Company name" required validationMessage={err('seller.companyName')}>
                        <Input value={form.seller.companyName} onChange={(_, d) => update((s) => { s.seller.companyName = d.value; })} />
                    </Field>
                    <Field label="VAT ID">
                        <Input value={form.seller.vatId} onChange={(_, d) => update((s) => { s.seller.vatId = d.value; })} />
                    </Field>
                    <Field label="SIREN (9 digits)" validationMessage={err('seller.siren')}>
                        <Input value={form.seller.siren} onChange={(_, d) => update((s) => { s.seller.siren = d.value; })} />
                    </Field>
                    <Field label="SIRET (14 digits)" validationMessage={err('seller.siret')}>
                        <Input value={form.seller.siret} onChange={(_, d) => update((s) => { s.seller.siret = d.value; })} />
                    </Field>
                    <Field label="Email">
                        <Input value={form.seller.email} onChange={(_, d) => update((s) => { s.seller.email = d.value; })} />
                    </Field>
                    <Field label="Phone">
                        <Input value={form.seller.phoneNumber} onChange={(_, d) => update((s) => { s.seller.phoneNumber = d.value; })} />
                    </Field>
                    <Field label="Address line 1" required validationMessage={err('seller.address')}>
                        <Input value={form.seller.address.line1} onChange={(_, d) => update((s) => { s.seller.address.line1 = d.value; })} />
                    </Field>
                    <Field label="City">
                        <Input value={form.seller.address.city} onChange={(_, d) => update((s) => { s.seller.address.city = d.value; })} />
                    </Field>
                    <Field label="Postal code">
                        <Input value={form.seller.address.postalCode} onChange={(_, d) => update((s) => { s.seller.address.postalCode = d.value; })} />
                    </Field>
                    <Field label="Address country">
                        <Input value={form.seller.address.countryCode} onChange={(_, d) => update((s) => { s.seller.address.countryCode = d.value.toUpperCase(); })} />
                    </Field>
                </div>
            </Card>

            {/* Buyer */}
            <Card className={styles.card}>
                <CardHeader image={<span className={styles.sectionBadge}><PersonRegular /></span>} header={<Subtitle2>{isTd17 ? 'Buyer (French Microsoft entity)' : 'Buyer (customer)'}</Subtitle2>} description={<Text size={200}>{isTd17 ? 'The French Microsoft entity receiving the purchase — drives routing' : 'The customer receiving the document'}</Text>} />
                <div className={styles.grid}>
                    {isTd17 && (
                        <Field label="French entity" required validationMessage={err('buyer.frenchEntity')} hint="Sets the buyer SIREN/VAT ID used for routing.">
                            <Dropdown
                                placeholder="Select French entity"
                                value={(() => {
                                    const e = FRENCH_ENTITY_BUYERS.find((x) => x.siren === form.buyer.siren);
                                    return e ? `${e.companyCode} — ${e.legalName}` : '';
                                })()}
                                selectedOptions={(() => {
                                    const e = FRENCH_ENTITY_BUYERS.find((x) => x.siren === form.buyer.siren);
                                    return e ? [e.companyCode] : [];
                                })()}
                                onOptionSelect={(_, data) => update((s) => {
                                    const ent = FRENCH_ENTITY_BUYERS.find((x) => x.companyCode === data.optionValue);
                                    if (ent) {
                                        s.buyer.companyName = ent.legalName;
                                        s.buyer.siren = ent.siren;
                                        s.buyer.vatId = ent.vatId;
                                        s.buyer.address.countryCode = 'FR';
                                    }
                                })}
                            >
                                {FRENCH_ENTITY_BUYERS.map((e) => (
                                    <Option key={e.companyCode} value={e.companyCode}>{`${e.companyCode} — ${e.legalName}`}</Option>
                                ))}
                            </Dropdown>
                        </Field>
                    )}
                    <Field label="Company name">
                        <Input value={form.buyer.companyName} onChange={(_, d) => update((s) => { s.buyer.companyName = d.value; })} />
                    </Field>
                    <Field label="VAT ID">
                        <Input value={form.buyer.vatId} onChange={(_, d) => update((s) => { s.buyer.vatId = d.value; })} />
                    </Field>
                    <Field label="SIREN (9 digits)" validationMessage={err('buyer.siren')}>
                        <Input value={form.buyer.siren} onChange={(_, d) => update((s) => { s.buyer.siren = d.value; })} />
                    </Field>
                    <Field label="SIRET (14 digits)" validationMessage={err('buyer.siret')}>
                        <Input value={form.buyer.siret} onChange={(_, d) => update((s) => { s.buyer.siret = d.value; })} />
                    </Field>
                    <Field label="Email">
                        <Input value={form.buyer.email} onChange={(_, d) => update((s) => { s.buyer.email = d.value; })} />
                    </Field>
                    <Field label="Address type" required>
                        <Dropdown
                            value={FRANCE_ADDRESS_TYPES.find((a) => a.value === form.buyer.addressType)?.label ?? ''}
                            selectedOptions={[form.buyer.addressType]}
                            onOptionSelect={(_, data) => update((s) => { s.buyer.addressType = data.optionValue as FranceAddressType; })}
                        >
                            {FRANCE_ADDRESS_TYPES.map((a) => (<Option key={a.value} value={a.value}>{a.label}</Option>))}
                        </Dropdown>
                    </Field>
                    <Field label="Address line 1">
                        <Input value={form.buyer.address.line1} onChange={(_, d) => update((s) => { s.buyer.address.line1 = d.value; })} />
                    </Field>
                    <Field label="City">
                        <Input value={form.buyer.address.city} onChange={(_, d) => update((s) => { s.buyer.address.city = d.value; })} />
                    </Field>
                    <Field label="Postal code">
                        <Input value={form.buyer.address.postalCode} onChange={(_, d) => update((s) => { s.buyer.address.postalCode = d.value; })} />
                    </Field>
                    <Field label="Address country">
                        <Input value={form.buyer.address.countryCode} onChange={(_, d) => update((s) => { s.buyer.address.countryCode = d.value.toUpperCase(); })} />
                    </Field>
                </div>
            </Card>

            {/* Line items (hidden for PaymentReceipt) */}
            {!isPaymentReceipt && (
                <Card className={styles.card}>
                    <CardHeader
                        image={<span className={styles.sectionBadge}><DocumentBulletListRegular /></span>}
                        header={<Subtitle2>Line items</Subtitle2>}
                        description={<Text size={200}>Amounts and tax are calculated automatically</Text>}
                        action={<Button className={styles.noWrapButton} appearance="secondary" icon={<AddRegular />} onClick={addLineItem}>Add line</Button>}
                    />
                    {err('lineItems') && (
                        <MessageBar intent="error"><MessageBarBody>{err('lineItems')}</MessageBarBody></MessageBar>
                    )}
                    <div style={{ marginTop: '12px' }}>
                        {form.lineItems.map((line, i) => (
                            <div key={line.key} className={styles.lineItem}>
                                <Field label="ID">
                                    <Input value={line.lineItemId} placeholder={String(i + 1)} onChange={(_, d) => update((s) => { s.lineItems[i].lineItemId = d.value; })} />
                                </Field>
                                <Field label="Description" required validationMessage={err(`lineItems[${i}].description`)}>
                                    <Input value={line.description} onChange={(_, d) => update((s) => { s.lineItems[i].description = d.value; })} />
                                </Field>
                                <Field label="Quantity" required validationMessage={err(`lineItems[${i}].quantity`)}>
                                    <Input type="number" value={line.quantity} onChange={(_, d) => update((s) => { s.lineItems[i].quantity = d.value; })} />
                                </Field>
                                <Field label="Unit price" required validationMessage={err(`lineItems[${i}].unitPrice`)}>
                                    <Input type="number" value={line.unitPrice} onChange={(_, d) => update((s) => { s.lineItems[i].unitPrice = d.value; })} />
                                </Field>
                                <Field label="VAT %" required validationMessage={err(`lineItems[${i}].vatRate`)}>
                                    <Dropdown
                                        value={line.vatRate === '' ? '' : String(line.vatRate)}
                                        selectedOptions={line.vatRate === '' ? [] : [String(line.vatRate)]}
                                        onOptionSelect={(_, data) => update((s) => { s.lineItems[i].vatRate = data.optionValue === undefined || data.optionValue === '' ? '' : Number(data.optionValue); })}
                                    >
                                        {VALID_FRENCH_VAT_RATES.map((r) => (<Option key={r} value={String(r)}>{`${r}%`}</Option>))}
                                    </Dropdown>
                                </Field>
                                <Button
                                    appearance="subtle"
                                    icon={<DeleteRegular />}
                                    aria-label={`Remove line ${i + 1}`}
                                    disabled={form.lineItems.length === 1}
                                    onClick={() => removeLineItem(i)}
                                />
                            </div>
                        ))}
                    </div>
                    <div className={styles.totalsBar}>
                        <div className={styles.totalsBox}>
                            <div className={styles.totalRow}>
                                <Text className={styles.totalLabel} size={200}>Total pre-tax</Text>
                                <Text className={styles.totalValue} size={300}>{formatMoney(totals.preTax)}</Text>
                            </div>
                            <div className={styles.totalRow}>
                                <Text className={styles.totalLabel} size={200}>Total VAT</Text>
                                <Text className={styles.totalValue} size={300}>{formatMoney(totals.tax)}</Text>
                            </div>
                            <div className={`${styles.totalRow} ${styles.grandTotalRow}`}>
                                <Text size={300}>Total</Text>
                                <Text className={styles.grandTotalValue} size={400}>{formatMoney(totals.total)}</Text>
                            </div>
                        </div>
                    </div>
                </Card>
            )}

            {/* Manual amounts (PaymentReceipt only) */}
            {isPaymentReceipt && (
                <Card className={styles.card}>
                    <CardHeader image={<span className={styles.sectionBadge}><MoneyRegular /></span>} header={<Subtitle2>Document amounts</Subtitle2>} description={<Text size={200}>Enter the totals for this payment receipt</Text>} />
                    <div className={styles.grid}>
                        <Field label="Pre-tax amount">
                            <Input type="number" value={form.manualAmounts.preTaxAmount} onChange={(_, d) => update((s) => { s.manualAmounts.preTaxAmount = d.value; })} />
                        </Field>
                        <Field label="Taxable amount">
                            <Input type="number" value={form.manualAmounts.taxableAmount} onChange={(_, d) => update((s) => { s.manualAmounts.taxableAmount = d.value; })} />
                        </Field>
                        <Field label="Tax amount">
                            <Input type="number" value={form.manualAmounts.taxAmount} onChange={(_, d) => update((s) => { s.manualAmounts.taxAmount = d.value; })} />
                        </Field>
                        <Field label="Total amount" required validationMessage={err('manualAmounts.totalAmount')}>
                            <Input type="number" value={form.manualAmounts.totalAmount} onChange={(_, d) => update((s) => { s.manualAmounts.totalAmount = d.value; })} />
                        </Field>
                    </div>
                </Card>
            )}

            {/* Reference (CreditNote only) */}
            {isCreditNote && (
                <Card className={styles.card}>
                    <CardHeader image={<span className={styles.sectionBadge}><LinkRegular /></span>} header={<Subtitle2>Original document reference</Subtitle2>} description={<Text size={200}>Credit notes must reference the original document</Text>} />
                    <div className={styles.grid}>
                        <Field label="Reference document ID" required validationMessage={err('reference.documentId')}>
                            <Input value={form.reference.documentId} onChange={(_, d) => update((s) => { s.reference.documentId = d.value; })} />
                        </Field>
                        <Field label="Reference date">
                            <Input type="date" value={form.reference.date} onChange={(_, d) => update((s) => { s.reference.date = d.value; })} />
                        </Field>
                    </div>
                </Card>
            )}

            {/* Actions */}
            <div className={styles.actionRow}>
                <Button appearance="primary" icon={<DocumentAddRegular />} onClick={handleCreatePayload} disabled={isValidating}>{isValidating ? 'Validating…' : 'Create Payload'}</Button>
                {payloadJson && (
                    <>
                        <Button appearance="secondary" icon={<CopyRegular />} onClick={handleCopy}>{copied ? 'Copied' : 'Copy JSON'}</Button>
                        <Button appearance="secondary" icon={<ArrowDownloadRegular />} onClick={handleDownload}>Download</Button>
                    </>
                )}
            </div>

            {errors && errors.length > 0 && (
                <MessageBar intent="error">
                    <MessageBarBody>
                        <MessageBarTitle>{`${errors.length} validation ${errors.length === 1 ? 'error' : 'errors'}`}</MessageBarTitle>
                        <ul className={styles.errorList}>
                            {errors.map((e, idx) => (<li key={`${e.field}-${idx}`}>{`${e.field}: ${e.message}`}</li>))}
                        </ul>
                    </MessageBarBody>
                </MessageBar>
            )}

            {serviceError && (
                <MessageBar intent="error">
                    <MessageBarBody>
                        <MessageBarTitle>Validation service unavailable</MessageBarTitle>
                        {serviceError}
                    </MessageBarBody>
                </MessageBar>
            )}

            {backendErrors && backendErrors.length > 0 && (() => {
                // Collapse identical code/message pairs so repeated validator failures
                // don't create noisy duplicate rows, then translate each to friendly guidance.
                const seen = new Set<string>();
                const friendly = backendErrors
                    .filter((e) => {
                        const key = `${e.code}|${e.message}`;
                        if (seen.has(key)) {
                            return false;
                        }
                        seen.add(key);
                        return true;
                    })
                    .map((e) => toFriendlyError(e.code, e.message));
                return (
                    <MessageBar intent="error">
                        <MessageBarBody>
                            <MessageBarTitle>{`Backend validation failed (${friendly.length} ${friendly.length === 1 ? 'issue' : 'issues'})`}</MessageBarTitle>
                            <ul className={styles.errorList}>
                                {friendly.map((f, idx) => (
                                    <li key={`${f.technicalCode}-${idx}`}>
                                        <strong>{f.title}</strong>
                                        <div className={styles.errorGuidance}>{f.guidance}</div>
                                        <div className={styles.errorDetail}>
                                            {f.recognized
                                                ? `Technical detail: ${f.technicalCode}: ${f.technicalMessage}`
                                                : `Technical code: ${f.technicalCode}`}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </MessageBarBody>
                    </MessageBar>
                );
            })()}

            {errors && errors.length === 0 && !backendErrors && !serviceError && payloadJson && (
                <MessageBar intent="success">
                    <MessageBarBody>
                        <MessageBarTitle>Payload created — content checks only</MessageBarTitle>
                        Validated against the backend France e-invoicing content and business rules. Review the payload below, then copy or download it.
                        {checksSkipped.length > 0 && (
                            <div className={styles.errorGuidance}>
                                {`Note: this dry run does not run every production ingestion check. Still applied at submission time: ${checksSkipped.map(friendlySkippedCheck).join(', ')}. A "valid" result here means the content is well-formed, not that submission is guaranteed to be accepted.`}
                            </div>
                        )}
                    </MessageBarBody>
                </MessageBar>
            )}

            {payloadJson && (
                <Card className={styles.card}>
                    <CardHeader image={<span className={styles.sectionBadge}><CodeRegular /></span>} header={<Subtitle2>Generated payload</Subtitle2>} description={<Text size={200}>Ready-to-submit transaction payload</Text>} />
                    <Label htmlFor="payload-output" style={{ display: 'none' }}>Generated payload JSON</Label>
                    <Textarea id="payload-output" className={styles.output} value={payloadJson} readOnly rows={22} />
                </Card>
            )}
        </div>
    );
}

export default ManualInvoice;
